import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Checkbox,
  Tooltip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  PlaylistAddCheck as BulkApproveIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function FuzzyMatchReview() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Selection for bulk operations
  const [selected, setSelected] = useState([]);

  // Dialogs
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Success/Error messages
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchStats();
    fetchMatches();
  }, [page, rowsPerPage]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/accounting/match-review/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching match review stats:', err);
    }
  };

  const fetchMatches = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}/api/accounting/match-review/pending?page=${page + 1}&limit=${rowsPerPage}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setMatches(response.data.data.matches || []);
        setTotalCount(response.data.data.pagination?.total || 0);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching pending matches:', err);
      setError(err.response?.data?.error || 'Failed to load matches');
      setLoading(false);
    }
  };

  const handleApprove = async (matchId) => {
    setProcessing(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/accounting/match-review/approve`,
        { match_id: matchId, notes: 'Approved via Match Review UI' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage(`Match approved - Journal Entry ${response.data.data.journal_entry_id} created`);
        fetchMatches();
        fetchStats();
        setSelected(selected.filter(id => id !== matchId));
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve match');
    }
    setProcessing(false);
  };

  const handleOpenRejectDialog = (match) => {
    setSelectedMatch(match);
    setRejectReason('');
    setError(null); setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/accounting/match-review/reject`,
        { match_id: selectedMatch.match_id, reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessage('Match rejected successfully');
        setRejectDialogOpen(false);
        fetchMatches();
        fetchStats();
        setSelected(selected.filter(id => id !== selectedMatch.match_id));
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject match');
    }
    setProcessing(false);
  };

  const handleBulkApprove = async () => {
    if (selected.length === 0) {
      setError('Please select matches to approve');
      return;
    }

    setProcessing(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/accounting/match-review/bulk-approve`,
        { match_ids: selected, notes: 'Bulk approved via Match Review UI' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { approved, failed, skipped } = response.data.data.summary;
        setSuccessMessage(
          `Bulk approve complete: ${approved} approved, ${failed} failed, ${skipped} skipped`
        );
        setSelected([]);
        fetchMatches();
        fetchStats();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to bulk approve');
    }
    setProcessing(false);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(matches.map(m => m.match_id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (matchId) => {
    if (selected.includes(matchId)) {
      setSelected(selected.filter(id => id !== matchId));
    } else {
      setSelected([...selected, matchId]);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box>
      {/* Summary Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending Review
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.summary.pending_review.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  matches need review
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Amount
                </Typography>
                <Typography variant="h4" color="error.main">
                  {formatCurrency(stats.summary.total_amount)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  stuck without JE
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Avg Difference
                </Typography>
                <Typography variant="h4">
                  ${stats.summary.avg_difference.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  amount variance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Oldest Match
                </Typography>
                <Typography variant="h5">
                  {formatDate(stats.summary.oldest_date)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  needs attention
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Action Bar */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Button
            variant="contained"
            color="success"
            startIcon={<BulkApproveIcon />}
            onClick={handleBulkApprove}
            disabled={selected.length === 0 || processing}
            sx={{ mr: 1 }}
          >
            Bulk Approve ({selected.length})
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchMatches(); fetchStats(); }}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
        <Typography variant="body2" color="textSecondary">
          Showing {matches.length} of {totalCount.toLocaleString()} pending matches
        </Typography>
      </Box>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < matches.length}
                    checked={matches.length > 0 && selected.length === matches.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Payout Date</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Bank Source</TableCell>
                <TableCell align="right">Payout Amount</TableCell>
                <TableCell align="right">Bank Amount</TableCell>
                <TableCell align="right">Difference</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Days Diff</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : matches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">
                      No pending matches to review
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                matches.map((match) => (
                  <TableRow
                    key={match.match_id}
                    hover
                    selected={selected.includes(match.match_id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(match.match_id)}
                        onChange={() => handleSelect(match.match_id)}
                      />
                    </TableCell>
                    <TableCell>{formatDate(match.payout_date)}</TableCell>
                    <TableCell>
                      {match.brand_name || (
                        <Chip label="Unknown" size="small" color="warning" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={match.bank_description || match.bank_account_name}>
                        <span>{match.bank_source}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(match.payout_amount, match.payout_currency)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(match.bank_amount, match.bank_currency)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${match.amount_difference >= 0 ? '+' : ''}${match.amount_difference.toFixed(2)}`}
                        size="small"
                        color={Math.abs(match.amount_difference) < 1 ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${match.confidence_score}%`}
                        size="small"
                        color={
                          match.confidence_score >= 80 ? 'success' :
                          match.confidence_score >= 60 ? 'warning' : 'error'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`Bank tx ${match.time_diff_days} days from payout`}>
                        <span>{match.time_diff_days}d</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Approve - Create Journal Entry">
                        <IconButton
                          color="success"
                          size="small"
                          onClick={() => handleApprove(match.match_id)}
                          disabled={processing}
                        >
                          <ApproveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleOpenRejectDialog(match)}
                          disabled={processing}
                        >
                          <RejectIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Match</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {selectedMatch && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Payout: {formatCurrency(selectedMatch.payout_amount, selectedMatch.payout_currency)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Bank: {formatCurrency(selectedMatch.bank_amount, selectedMatch.bank_currency)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Difference: {selectedMatch.amount_difference.toFixed(2)}
              </Typography>
            </Box>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Please provide a reason for rejecting this match..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={processing || !rejectReason.trim()}
          >
            {processing ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
